import './DeviceDetailsView.scss'

import { useBoolState, useFn } from '@eturi/react'
import { useT } from '@eturi/react-i18next'
import {
	deviceBattery$,
	deviceChildAppStatus$,
	deviceOnlineStatus$,
	devicePairStatus$,
	devicePairTs$,
	deviceScreenTimeViewData$,
	deviceTamperproofed$,
	isInstallingChildApp$,
} from '@op/app-shared'
import type { DeviceBlockStatus, ManagedDevice, ManagedUser, RawManagedUser } from '@op/services'
import { isIOSDevice, removeDevice } from '@op/services'
import { useNavTo } from '../../hooks'
import { setPairModalType } from '../../reducers/pair.slice'
import { useAppDispatch, useTypedSelector } from '../../types'
import { ActionBtn, ModalFooter, useToasts } from '../../widgets'
import { AvatarImage } from '../Avatar'
import ChildAppInstallModal from './ChildAppInstallModal'
import {
	DeviceBatteryLife,
	DeviceChildAppStatus,
	DeviceOnlineStatus,
	DevicePairDate,
	DevicePairStatus,
	DeviceTamperproofStatus,
	ScreenTimeInfoRow,
} from './DeviceInfoFields'
import TamperproofModal from './TamperproofModal'

type DeviceViewProps = {
	readonly device: ManagedDevice
	readonly deviceStatus: DeviceBlockStatus
	readonly onClose: () => void
	readonly onAndroidRemoveManagement: () => void
	readonly user: RawManagedUser
}

export const DeviceDetailsView = ({
	deviceStatus,
	device,
	onAndroidRemoveManagement,
	user,
	onClose,
}: DeviceViewProps) => {
	const d = useAppDispatch()
	const navTo = useNavTo()
	const [t] = useT('devices.device')
	const { addConfirmToast, addErrorToast } = useToasts()

	const [isDeletingDevice, setDeleting, cancelDeleting] = useBoolState(false)

	const deviceName = device.displayName
	const userName = user.user_name || ''

	const childApp = useTypedSelector((s) => deviceChildAppStatus$(s, device, deviceStatus))
	const battery = useTypedSelector((s) => deviceBattery$(s, device))
	const onlineStatus = useTypedSelector((s) => deviceOnlineStatus$(s, device))
	const pairStatus = useTypedSelector((s) => devicePairStatus$(s, device))
	const pairTs = useTypedSelector((s) => devicePairTs$(s, device, deviceStatus))
	const screenTime = useTypedSelector((s) => deviceScreenTimeViewData$(s, deviceStatus))
	const tamperproofed = useTypedSelector((s) => deviceTamperproofed$(s, device))
	const isInstallingChildApp = useTypedSelector((s) =>
		isInstallingChildApp$(s, { deviceId: device.device_id, userId: user.user_id }),
	)

	const [isChildAppModalVisible, showChildAppModal, hideChildAppModal] = useBoolState(false)
	const [isTamperproofModalVisible, showTamperproofModal, hideTamperproofModal] =
		useBoolState(false)

	// Once user has confirmed delete we go through and make the delete requests
	const handleConfirmDeleteDevice = useFn(async () => {
		const translationOpt = { child: userName, deviceName }

		try {
			await d(removeDevice(device.device_id)).unwrap()

			onClose()
		} catch {
			addErrorToast(t.$('messages.delete_device_error', translationOpt))
		}
	})

	// Initiates the "delete" flow by first prompting with a confirmation toast
	const handleDeleteDevice = useFn(() => {
		if (isDeletingDevice) return

		setDeleting()

		// Check to see if it is a deletable android device
		const cannotDeleteDevice =
			!isIOSDevice(device) &&
			!device.management_removed &&
			Number.parseInt(device.app_version, 10) < 4

		if (cannotDeleteDevice) {
			// If the device cannot be deleted (android management needs to be
			// removed first) then we show a modal with instructions. Since this
			// is currently displayed in a modal we must show that modal and
			// call close
			onAndroidRemoveManagement()
			return onClose()
		}

		addConfirmToast({
			msg: t.$('actions.delete_confirmation', { value: deviceName }),
			onConfirm: handleConfirmDeleteDevice,
			onCancel: cancelDeleting,
			onClose: cancelDeleting,
		})
	})

	const handleFullPairPress = useFn(() => {
		navTo('/connect')
	})

	const handleQuickPairPress = useFn(() => {
		d(setPairModalType('quick_pair'))
	})
	return (
		<>
			<div className="flex flex-col items-center gap-2 mb-10">
				<AvatarImage className="h-18 w-18 mr-3 flex-shrink-0" user={user as ManagedUser} />
				<h3 className="capitalize">{t('device_modal_header', { userName })}</h3>
			</div>

			<div className="flex flex-col">
				<ScreenTimeInfoRow screenTime={screenTime} />
				<DeviceOnlineStatus status={onlineStatus} />
				<DeviceBatteryLife battery={battery} />

				<DeviceChildAppStatus
					childApp={childApp}
					isInstalling={isInstallingChildApp}
					onPress={showChildAppModal}
				/>

				<DeviceTamperproofStatus
					tamperproofed={tamperproofed}
					onPress={tamperproofed ? undefined : showTamperproofModal}
				/>

				<DevicePairStatus
					pairStatus={pairStatus}
					onPress={
						pairStatus == 'full_pair' ? undefined
						: pairStatus == 'quick_pair' ?
							handleFullPairPress
						:	handleQuickPairPress
					}
				/>

				<DevicePairDate pairTs={pairTs} />
			</div>

			{isChildAppModalVisible && (
				<ChildAppInstallModal
					childId={user.user_id}
					deviceId={device.device_id}
					onClose={hideChildAppModal}
				/>
			)}

			{isTamperproofModalVisible && <TamperproofModal onClose={hideTamperproofModal} />}

			<ModalFooter>
				<ActionBtn
					color="red"
					className="absolute right-5 bottom-5"
					icon="trash"
					invert
					onClick={handleDeleteDevice}
				/>
			</ModalFooter>
		</>
	)
}
